import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ListPageSkeleton } from '../components/skeletons/ListPageSkeleton';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { PageHeader } from '../components/design-system/PageHeader';
import { Mail, UserPlus, Users, UserX, CheckCircle, Trash2, UserCheck, Search, X, Upload, HelpCircle, Download, FileText, Loader, AlertTriangle, MoreVertical, Menu, Settings, LogOut, Pencil, Tag } from 'lucide-react';
import { FileUploadZone } from '../components/FileUploadZone';
import { ImportPreview } from '../components/ImportPreview';
import {
  parseImportText,
  ParsedContact,
  calculateStats,
  generateExampleCSV,
  generateExampleText,
  generateExampleExcel,
} from '../lib/emailImportHelpers';

interface Contact {
  id: string;
  email: string;
  name: string;
  subscribed: boolean;
  created_at: string;
  tags: string[];
}

interface Stats {
  total: number;
  active: number;
  unsubscribed: number;
}

type ImportStep = 'input' | 'preview' | 'importing';
type BulkDeleteType = 'all' | 'unsubscribed' | 'subscribed' | null;

export function Mailinglist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState<Stats>({ total: 0, active: 0, unsubscribed: 0 });
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'unsubscribed'>('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState<ImportStep>('input');
  const [importText, setImportText] = useState('');
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState<BulkDeleteType>(null);
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState('');
  const [bulkDeleteStep, setBulkDeleteStep] = useState<'input' | 'confirm'>('input');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [browseLimit, setBrowseLimit] = useState(25);
  const [newTag, setNewTag] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [importTag, setImportTag] = useState('');
  const [tagToRemove, setTagToRemove] = useState<string | null>(null);
  const [removingTag, setRemovingTag] = useState(false);

  useEffect(() => {
    setBrowseLimit(25);
  }, [filterStatus, searchQuery, filterTag]);

  // Alle bestaande groepen (voor dropdowns/datalists), afgeleid uit de geladen contacten.
  const allTags = Array.from(new Set(contacts.flatMap((c) => c.tags || []))).sort((a, b) =>
    a.localeCompare(b, 'nl')
  );

  const browseContacts = contacts.filter((c) => {
    if (filterStatus === 'active' && !c.subscribed) return false;
    if (filterStatus === 'unsubscribed' && c.subscribed) return false;
    if (filterTag && !(c.tags || []).includes(filterTag)) return false;
    return true;
  });

  const focusContactList = () => {
    setSearchQuery('');
    requestAnimationFrame(() => {
      document.getElementById('contacten-beheren')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        searchContacts();
      } else {
        setFilteredContacts([]);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, filterStatus, filterTag]);

  const searchContacts = async () => {
    if (!user || !searchQuery.trim()) {
      setFilteredContacts([]);
      return;
    }

    try {
      setSearching(true);
      const query = searchQuery.trim();

      let dbQuery = supabase
        .from('email_contacts')
        .select('*')
        .eq('user_id', user.id);

      if (filterStatus === 'active') {
        dbQuery = dbQuery.eq('subscribed', true);
      } else if (filterStatus === 'unsubscribed') {
        dbQuery = dbQuery.eq('subscribed', false);
      }

      if (filterTag) {
        dbQuery = dbQuery.overlaps('tags', [filterTag]);
      }

      dbQuery = dbQuery.or(`email.ilike.%${query}%,name.ilike.%${query}%`);
      dbQuery = dbQuery.order('created_at', { ascending: false }).limit(100);

      const { data, error } = await dbQuery;

      if (error) {
        console.error('Email contacts search error:', error);
        setFilteredContacts([]);
      } else {
        setFilteredContacts(data || []);
      }
    } catch (err) {
      console.error('Email contacts search exception:', err);
      setFilteredContacts([]);
    } finally {
      setSearching(false);
    }
  };

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Separate COUNT queries for accurate stats (works even with >10k contacts)
      const { count: totalCount, error: totalError } = await supabase
        .from('email_contacts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const { count: activeCount, error: activeError } = await supabase
        .from('email_contacts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('subscribed', true);

      if (totalError) {
        console.error('Error loading total count:', totalError);
      } else if (activeError) {
        console.error('Error loading active count:', activeError);
      }

      // Set stats using count results
      const total = totalCount || 0;
      const active = activeCount || 0;
      const unsubscribed = total - active;
      setStats({ total, active, unsubscribed });

      // Load contacts (limited for performance, but stats are accurate)
      const { data: contacts, error: contactsError } = await supabase
        .from('email_contacts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10000);

      if (contactsError) {
        console.error('Error loading contacts:', contactsError);
      } else if (contacts) {
        setContacts(contacts);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const addContact = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !newEmail.trim()) {
      setErrorMessage('Email adres is verplicht');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      setErrorMessage('Voer een geldig email adres in');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const { error } = await supabase.from('email_contacts').insert({
        user_id: user.id,
        email: newEmail.toLowerCase().trim(),
        name: newName.trim(),
        subscribed: true,
        tags: newTag.trim() ? [newTag.trim()] : [],
      });

      if (error) {
        if (error.code === '23505') {
          setErrorMessage('Dit email adres bestaat al in je lijst');
        } else {
          throw error;
        }
      } else {
        setSuccessMessage('Contact succesvol toegevoegd!');
        setNewEmail('');
        setNewName('');
        setNewTag('');
        setSearchQuery('');
        await loadData();
        if (searchQuery.trim()) {
          searchContacts();
        }

        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error adding contact:', err);
      setErrorMessage('Er ging iets mis bij het toevoegen van het contact');
    } finally {
      setSaving(false);
    }
  };

  const toggleSubscription = async (contact: Contact) => {
    try {
      const { error } = await supabase
        .from('email_contacts')
        .update({ subscribed: !contact.subscribed })
        .eq('id', contact.id);

      if (error) {
        throw error;
      }

      setSuccessMessage(
        contact.subscribed
          ? 'Contact uitgeschreven'
          : 'Contact ingeschreven'
      );
      loadData();
      if (searchQuery.trim()) {
        searchContacts();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling subscription:', err);
      setErrorMessage('Er ging iets mis bij het wijzigen van de subscription status');
    }
  };

  const deleteContact = (contact: Contact) => {
    setErrorMessage('');
    setContactToDelete(contact);
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete) return;

    try {
      setDeleting(true);
      setErrorMessage('');

      const { error } = await supabase.from('email_contacts').delete().eq('id', contactToDelete.id);

      if (error) {
        throw error;
      }

      setSuccessMessage('Contact verwijderd');
      setContactToDelete(null);
      loadData();
      if (searchQuery.trim()) {
        searchContacts();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting contact:', err);
      setErrorMessage('Er ging iets mis bij het verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  const openEditContact = (contact: Contact) => {
    setEditName(contact.name || '');
    setEditTags(contact.tags || []);
    setEditTagInput('');
    setErrorMessage('');
    setContactToEdit(contact);
  };

  const addEditTag = () => {
    const tag = editTagInput.trim();
    if (!tag) return;
    if (!editTags.includes(tag)) {
      setEditTags([...editTags, tag]);
    }
    setEditTagInput('');
  };

  const confirmEditContact = async () => {
    if (!contactToEdit) return;

    try {
      setSavingEdit(true);
      setErrorMessage('');

      // Nog niet bevestigde invoer in het groepen-veld telt gewoon mee.
      const pendingTag = editTagInput.trim();
      const finalTags = pendingTag && !editTags.includes(pendingTag) ? [...editTags, pendingTag] : editTags;

      const { error } = await supabase
        .from('email_contacts')
        .update({ name: editName.trim(), tags: finalTags })
        .eq('id', contactToEdit.id);

      if (error) {
        throw error;
      }

      setSuccessMessage('Contact bijgewerkt');
      setContactToEdit(null);
      loadData();
      if (searchQuery.trim()) {
        searchContacts();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating contact name:', err);
      setErrorMessage('Er ging iets mis bij het bijwerken van de naam');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleFileRead = (content: string, fileName: string) => {
    setImportText(content);
    handleParseImport(content);
  };

  const handleParseImport = (text?: string) => {
    const textToParse = text || importText;
    if (!textToParse.trim()) return;

    const existingEmails = contacts.map((c) => c.email);
    const { contacts: parsed } = parseImportText(textToParse, existingEmails);

    setParsedContacts(parsed);
    setImportStep('preview');
  };

  const handleRemoveContact = (email: string) => {
    setParsedContacts((prev) => prev.filter((c) => c.email !== email));
  };

  const handleRemoveAllInvalid = () => {
    setParsedContacts((prev) => prev.filter((c) => c.status !== 'invalid'));
  };

  const handleImport = async () => {
    if (!user) return;

    const validContacts = parsedContacts.filter((c) => c.status === 'valid');
    if (validContacts.length === 0) {
      alert('Geen geldige contacten om te importeren');
      return;
    }

    try {
      setImportStep('importing');
      setImportProgress({ current: 0, total: validContacts.length });

      const batchSize = 50;
      let imported = 0;

      for (let i = 0; i < validContacts.length; i += batchSize) {
        const batch = validContacts.slice(i, i + batchSize);
        const contactsToImport = batch.map((c) => ({
          user_id: user.id,
          email: c.email,
          name: c.name,
          subscribed: true,
        }));

        const { error } = await supabase
          .from('email_contacts')
          .upsert(contactsToImport, { onConflict: 'user_id,email', ignoreDuplicates: true });

        if (error) {
          console.error('Batch import error:', error);
          throw error;
        }

        imported += batch.length;
        setImportProgress({ current: imported, total: validContacts.length });
      }

      // Groep toekennen aan de hele import — via RPC zodat bestaande contacten
      // (upsert met ignoreDuplicates slaat die over) de groep óók krijgen,
      // zonder hun huidige groepen te verliezen.
      const tag = importTag.trim();
      if (tag) {
        const allEmails = validContacts.map((c) => c.email);
        for (let i = 0; i < allEmails.length; i += 500) {
          const { error: tagError } = await supabase.rpc('add_tag_to_email_contacts', {
            p_user_id: user.id,
            p_emails: allEmails.slice(i, i + 500),
            p_tag: tag,
          });
          if (tagError) {
            console.error('Tag import error:', tagError);
            throw tagError;
          }
        }
      }

      setSuccessMessage(
        tag
          ? `Gelukt! ${imported} contacten geïmporteerd in groep "${tag}".`
          : `Gelukt! ${imported} contacten geïmporteerd.`
      );
      resetImportModal();
      loadData();
      if (searchQuery.trim()) {
        searchContacts();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error importing contacts:', err);
      setErrorMessage('Er ging iets mis bij het importeren. Probeer het opnieuw.');
      setImportStep('preview');
    }
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setImportStep('input');
    setImportText('');
    setParsedContacts([]);
    setImportProgress({ current: 0, total: 0 });
    setImportTag('');
  };

  const confirmRemoveTag = async () => {
    if (!user || !tagToRemove) return;

    try {
      setRemovingTag(true);
      setErrorMessage('');

      const { data: updatedCount, error } = await supabase.rpc('remove_tag_from_email_contacts', {
        p_user_id: user.id,
        p_tag: tagToRemove,
      });

      if (error) {
        throw error;
      }

      setSuccessMessage(`Groep "${tagToRemove}" verwijderd bij ${updatedCount ?? 0} contact${updatedCount === 1 ? '' : 'en'}`);
      if (filterTag === tagToRemove) {
        setFilterTag('');
      }
      setTagToRemove(null);
      loadData();
      if (searchQuery.trim()) {
        searchContacts();
      }
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error removing tag:', err);
      setErrorMessage('Er ging iets mis bij het verwijderen van de groep');
    } finally {
      setRemovingTag(false);
    }
  };

  const downloadExample = (type: 'csv' | 'txt' | 'xlsx') => {
    let blob: Blob;
    let filename: string;

    if (type === 'csv') {
      const content = generateExampleCSV();
      blob = new Blob([content], { type: 'text/csv' });
      filename = 'voorbeeld.csv';
    } else if (type === 'xlsx') {
      blob = generateExampleExcel();
      filename = 'voorbeeld.xlsx';
    } else {
      const content = generateExampleText();
      blob = new Blob([content], { type: 'text/plain' });
      filename = 'voorbeeld.txt';
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getBulkDeleteCount = () => {
    if (bulkDeleteType === 'all') {
      return stats.total;
    } else if (bulkDeleteType === 'unsubscribed') {
      return stats.unsubscribed;
    } else if (bulkDeleteType === 'subscribed') {
      return stats.active;
    }
    return 0;
  };

  const openBulkDeleteModal = (type: BulkDeleteType) => {
    setBulkDeleteType(type);
    setBulkDeleteStep('input');
    setBulkDeleteConfirmation('');
    setShowBulkDeleteModal(true);
  };

  const closeBulkDeleteModal = () => {
    setShowBulkDeleteModal(false);
    setBulkDeleteType(null);
    setBulkDeleteStep('input');
    setBulkDeleteConfirmation('');
    setBulkDeleting(false);
  };

  const handleBulkDelete = async () => {
    if (!user || !bulkDeleteType) return;

    try {
      setBulkDeleting(true);
      setErrorMessage('');

      let query = supabase
        .from('email_contacts')
        .delete()
        .eq('user_id', user.id);

      if (bulkDeleteType === 'unsubscribed') {
        query = query.eq('subscribed', false);
      } else if (bulkDeleteType === 'subscribed') {
        query = query.eq('subscribed', true);
      }

      const { error, count } = await query;

      if (error) {
        throw error;
      }

      const deletedCount = getBulkDeleteCount();
      setSuccessMessage(`${deletedCount} contact${deletedCount !== 1 ? 'en' : ''} succesvol verwijderd`);
      closeBulkDeleteModal();
      loadData();
      if (searchQuery.trim()) {
        searchContacts();
      }
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error bulk deleting contacts:', err);
      setErrorMessage('Er ging iets mis bij het verwijderen. Probeer het opnieuw.');
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <Sidebar onSignOut={handleSignOut} />
        <MobileNav
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          onSignOut={handleSignOut}
        />
        <div className="lg:pl-64">
          <MaintenanceBanner />
          <LowCreditsBanner />
          <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Menu"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                  <img
                    src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                    alt="Dibbi Logo"
                    className="h-8 lg:hidden"
                  />
                </div>
                <div className="flex items-center gap-3 lg:hidden">
                  <button
                    onClick={() => navigate('/dashboard/settings')}
                    className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Instellingen"
                  >
                    <Settings className="w-6 h-6" />
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Uitloggen"
                  >
                    <LogOut className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </header>
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
            <ListPageSkeleton rows={6} withTabs={false} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Sidebar onSignOut={handleSignOut} />
      <MobileNav
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onSignOut={handleSignOut}
      />
      <div className="lg:pl-64">
        <MaintenanceBanner />
        <LowCreditsBanner />
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                  alt="Dibbi Logo"
                  className="h-8 lg:hidden"
                />
              </div>
              <div className="flex items-center gap-3 lg:hidden">
                <button
                  onClick={() => navigate('/dashboard/settings')}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Instellingen"
                >
                  <Settings className="w-6 h-6" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
        <div className="space-y-6">
          <PageHeader
            title="Mailinglijst"
            subtitle="Beheer je email contacten en bouw je lijst op"
          />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <button
            type="button"
            onClick={() => { setFilterStatus('all'); focusContactList(); }}
            className={`card text-left transition-all hover:border-[#FF6B35] hover:shadow-md ${filterStatus === 'all' ? 'ring-2 ring-[#FF6B35]' : ''}`}
            aria-label="Toon alle contacten"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Totaal Contacten</p>
              <Mail className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-[#231F20]">{stats.total}</p>
          </button>

          <button
            type="button"
            onClick={() => { setFilterStatus('active'); focusContactList(); }}
            className={`card text-left transition-all hover:border-green-500 hover:shadow-md ${filterStatus === 'active' ? 'ring-2 ring-green-500' : ''}`}
            aria-label="Toon actieve contacten"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Actief</p>
              <Users className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-green-600">{stats.active}</p>
          </button>

          <button
            type="button"
            onClick={() => { setFilterStatus('unsubscribed'); focusContactList(); }}
            className={`card text-left transition-all hover:border-gray-500 hover:shadow-md ${filterStatus === 'unsubscribed' ? 'ring-2 ring-gray-500' : ''}`}
            aria-label="Toon uitgeschreven contacten"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Uitgeschreven</p>
              <UserX className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-gray-500">{stats.unsubscribed}</p>
          </button>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#231F20]">Nieuw contact toevoegen</h2>
              <p className="text-sm text-gray-600">Voeg één email adres toe aan je lijst</p>
            </div>
          </div>

          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          {errorMessage && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <UserX className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={addContact} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Adres <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="naam@voorbeeld.nl"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Naam (optioneel)
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Jan"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Groep (optioneel)
                </label>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="bijv. klanten"
                  list="mailinglist-groepen"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            <datalist id="mailinglist-groepen">
              {allTags.map((tag) => (
                <option key={tag} value={tag} />
              ))}
            </datalist>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={saving || !newEmail.trim()}
                className="px-6 py-3 bg-[#FF6B35] text-white font-medium rounded-lg hover:bg-[#E55A2B] disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                {saving ? 'Toevoegen...' : 'Voeg contact toe'}
              </button>
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                className="px-6 py-3 bg-white text-[#231F20] font-medium rounded-lg border-2 border-[#231F20] hover:bg-[#231F20] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Importeer meerdere contacten
              </button>
            </div>
          </form>
        </div>

        <div className="card" id="contacten-beheren">
          <h2 className="text-xl font-bold text-[#231F20] mb-6">Contacten beheren</h2>

          {contacts.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">Nog geen contacten</p>
              <p className="text-gray-400 text-sm">
                Voeg je eerste contact toe met het formulier hierboven
              </p>
            </div>
          ) : (
            <>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Zoek op naam of email..."
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === 'all'
                        ? 'bg-[#FF6B35] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Alle
                  </button>
                  <button
                    onClick={() => setFilterStatus('active')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === 'active'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Actief
                  </button>
                  <button
                    onClick={() => setFilterStatus('unsubscribed')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === 'unsubscribed'
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Uitgeschreven
                  </button>
                  {allTags.length > 0 && (
                    <select
                      value={filterTag}
                      onChange={(e) => setFilterTag(e.target.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border-0 outline-none cursor-pointer transition-colors ${
                        filterTag
                          ? 'bg-[#231F20] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      aria-label="Filter op groep"
                    >
                      <option value="">Alle groepen</option>
                      {allTags.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {contacts.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => setShowActionsMenu(!showActionsMenu)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                      Meer acties
                    </button>

                    {showActionsMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowActionsMenu(false)}
                        />
                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                          <div className="px-4 py-2 border-b border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Verwijderingsopties
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              openBulkDeleteModal('subscribed');
                              setShowActionsMenu(false);
                            }}
                            disabled={stats.active === 0}
                            className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <UserCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Verwijder actieve contacten
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {stats.active} {stats.active === 1 ? 'contact' : 'contacten'} met actieve inschrijving
                              </p>
                            </div>
                          </button>
                          <button
                            onClick={() => {
                              openBulkDeleteModal('unsubscribed');
                              setShowActionsMenu(false);
                            }}
                            disabled={stats.unsubscribed === 0}
                            className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <UserX className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Verwijder uitgeschreven contacten
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {stats.unsubscribed} uitgeschreven {stats.unsubscribed === 1 ? 'contact' : 'contacten'}
                              </p>
                            </div>
                          </button>
                          {allTags.length > 0 && (
                            <button
                              onClick={() => {
                                setTagToRemove(allTags[0]);
                                setShowActionsMenu(false);
                              }}
                              className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                            >
                              <Tag className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Groep verwijderen
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Haal een groep weg bij alle contacten (contacten blijven bestaan)
                                </p>
                              </div>
                            </button>
                          )}
                          <div className="border-t border-gray-100 mt-2 pt-2">
                            <button
                              onClick={() => {
                                openBulkDeleteModal('all');
                                setShowActionsMenu(false);
                              }}
                              className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-red-900">
                                  Verwijder alle contacten
                                </p>
                                <p className="text-xs text-red-600 mt-0.5">
                                  {stats.total} {stats.total === 1 ? 'contact' : 'contacten'} permanent verwijderen
                                </p>
                              </div>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {(() => {
                const isSearching = !!searchQuery.trim();
                const list = isSearching ? filteredContacts : browseContacts;
                const visible = isSearching ? list.slice(0, 50) : list.slice(0, browseLimit);
                const filterLabel =
                  filterStatus === 'active' ? 'actieve' :
                  filterStatus === 'unsubscribed' ? 'uitgeschreven' : '';

                if (!isSearching && list.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-lg text-gray-600 mb-2">
                        Geen {filterLabel} contacten
                      </p>
                      <p className="text-sm text-gray-500">
                        Kies een ander filter of voeg contacten toe.
                      </p>
                    </div>
                  );
                }

                if (isSearching && list.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600 mb-1">
                        {searching ? 'Bezig met zoeken...' : 'Geen resultaten gevonden'}
                      </p>
                      <p className="text-sm text-gray-500">Probeer een andere zoekopdracht</p>
                    </div>
                  );
                }

                return (
                <div className="space-y-2">
                  {!isSearching && (
                    <p className="text-sm text-gray-500 pb-2">
                      {list.length} {filterLabel || 'contact'}{list.length !== 1 ? 'en' : ''} — gesorteerd op nieuwste eerst
                    </p>
                  )}
                  {visible.map((contact) => (
                    <div
                      key={contact.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-[#FF6B35] transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-[#231F20] truncate">
                              {contact.name || contact.email}
                            </p>
                            {contact.subscribed ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                                <CheckCircle className="w-3 h-3" />
                                Actief
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex-shrink-0">
                                <UserX className="w-3 h-3" />
                                Uit
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 truncate">{contact.email}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(contact.created_at).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          {(contact.tags || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {contact.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#FF6B35]/10 text-[#B84A1F]"
                                >
                                  <Tag className="w-3 h-3" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => openEditContact(contact)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="Contact bewerken"
                            title="Contact bewerken"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => toggleSubscription(contact)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={contact.subscribed ? 'Uitschrijven' : 'Inschrijven'}
                          >
                            {contact.subscribed ? (
                              <UserX className="w-5 h-5" />
                            ) : (
                              <UserCheck className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => deleteContact(contact)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Verwijderen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {isSearching && filteredContacts.length > 50 && (
                    <p className="text-center text-sm text-gray-500 py-2">
                      Toon eerste 50 van {filteredContacts.length === 100 ? '100+' : filteredContacts.length} resultaten. Verfijn je zoekopdracht voor meer specifieke resultaten.
                    </p>
                  )}
                  {!isSearching && list.length > browseLimit && (
                    <div className="flex justify-center pt-2">
                      <button
                        onClick={() => setBrowseLimit((n) => n + 25)}
                        className="px-4 py-2 text-sm font-medium text-[#231F20] bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Toon meer ({list.length - browseLimit} resterend)
                      </button>
                    </div>
                  )}
                </div>
                );
              })()}
            </>
          )}
        </div>
        </div>
      </main>
      </div>
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full my-8 p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#231F20] mb-1">Contacten importeren</h2>
                <p className="text-sm text-gray-600">
                  {importStep === 'input' && 'Upload een bestand of plak je contacten'}
                  {importStep === 'preview' && 'Controleer je contacten voor import'}
                  {importStep === 'importing' && 'Bezig met importeren...'}
                </p>
              </div>
              <button
                onClick={resetImportModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={importStep === 'importing'}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {importStep === 'input' && (
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900 text-sm mb-1">Ondersteunde formaten</p>
                      <p className="text-xs text-blue-800 mb-2">CSV, Excel (.xls, .xlsx), tekstbestanden of plak vanuit Excel</p>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => downloadExample('csv')}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          CSV
                        </button>
                        <button
                          onClick={() => downloadExample('xlsx')}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          Excel
                        </button>
                        <button
                          onClick={() => downloadExample('txt')}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          TXT
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-[#231F20] mb-2 flex items-center gap-2 text-sm">
                      <Upload className="w-4 h-4" />
                      Optie 1: Upload bestand
                    </h3>
                    <FileUploadZone onFileRead={handleFileRead} />
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="text-xs text-gray-500 font-medium">OF</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-[#231F20] mb-2 flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4" />
                      Optie 2: Plak tekst
                    </h3>
                    <textarea
                      value={importText}
                      onChange={(e) => setImportText(e.target.value)}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all font-mono text-sm"
                      placeholder="jan@example.com,Jan&#10;piet@bedrijf.nl,Piet&#10;&#10;Of gewoon één email per regel:&#10;jan@example.com&#10;piet@bedrijf.nl"
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <button onClick={resetImportModal} className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors text-sm">
                    Annuleer
                  </button>
                  <button
                    onClick={() => handleParseImport()}
                    disabled={!importText.trim()}
                    className="px-4 py-2 bg-[#FF6B35] text-white font-medium rounded-lg hover:bg-[#E55A2B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Volgende: Preview
                  </button>
                </div>
              </div>
            )}

            {importStep === 'preview' && (
              <div className="space-y-4">
                <ImportPreview
                  contacts={parsedContacts}
                  stats={calculateStats(parsedContacts)}
                  onRemoveContact={handleRemoveContact}
                  onRemoveAllInvalid={handleRemoveAllInvalid}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Tag className="w-4 h-4" />
                      Groep toekennen (optioneel)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={importTag}
                    onChange={(e) => setImportTag(e.target.value)}
                    placeholder="bijv. klanten"
                    list="mailinglist-groepen"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Alle contacten uit deze import komen in deze groep — ook contacten die al in je lijst staan (hun bestaande groepen blijven behouden).
                  </p>
                </div>

                <div className="flex gap-2 justify-end">
                  <button onClick={() => setImportStep('input')} className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors text-sm">
                    Terug
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={calculateStats(parsedContacts).valid === 0}
                    className="px-4 py-2 bg-[#FF6B35] text-white font-medium rounded-lg hover:bg-[#E55A2B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Importeer {calculateStats(parsedContacts).valid} contact{calculateStats(parsedContacts).valid !== 1 ? 'en' : ''}
                  </button>
                </div>
              </div>
            )}

            {importStep === 'importing' && (
              <div className="py-12">
                <div className="text-center">
                  <Loader className="w-16 h-16 text-[#FF6B35] mx-auto mb-4 animate-spin" />
                  <p className="text-xl font-semibold text-[#231F20] mb-2">
                    Contacten importeren...
                  </p>
                  <p className="text-gray-600 mb-4">
                    {importProgress.current} van {importProgress.total} verwerkt
                  </p>
                  <div className="max-w-md mx-auto bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-[#FF6B35] h-full transition-all duration-300"
                      style={{
                        width: `${(importProgress.current / importProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#231F20] mb-2">
                  {bulkDeleteType === 'all' && 'Alle Contacten Verwijderen'}
                  {bulkDeleteType === 'unsubscribed' && 'Uitgeschreven Contacten Verwijderen'}
                  {bulkDeleteType === 'subscribed' && 'Actieve Contacten Verwijderen'}
                </h2>
                <p className="text-sm text-gray-600">
                  {bulkDeleteType === 'all' && 'Je staat op het punt alle contacten permanent te verwijderen'}
                  {bulkDeleteType === 'unsubscribed' && 'Je verwijdert alleen contacten die zich hebben uitgeschreven'}
                  {bulkDeleteType === 'subscribed' && 'Je verwijdert alleen contacten met actieve inschrijving'}
                </p>
              </div>
              <button
                onClick={closeBulkDeleteModal}
                disabled={bulkDeleting}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {bulkDeleteStep === 'input' && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-900 font-semibold mb-2">
                    Je staat op het punt {getBulkDeleteCount()} contact{getBulkDeleteCount() !== 1 ? 'en' : ''} te verwijderen
                  </p>
                  <ul className="text-sm text-red-800 space-y-1">
                    {bulkDeleteType === 'all' && (
                      <>
                        <li>• Alle contacten (actief én uitgeschreven) worden permanent verwijderd</li>
                        <li>• Je mailinglijst wordt volledig leeggemaakt</li>
                      </>
                    )}
                    {bulkDeleteType === 'unsubscribed' && (
                      <>
                        <li>• Alleen uitgeschreven contacten worden verwijderd</li>
                        <li>• Actieve contacten blijven behouden</li>
                      </>
                    )}
                    {bulkDeleteType === 'subscribed' && (
                      <>
                        <li>• Alleen actieve contacten worden verwijderd</li>
                        <li>• Uitgeschreven contacten blijven behouden</li>
                      </>
                    )}
                    <li>• Deze actie kan NIET ongedaan gemaakt worden</li>
                    <li>• Er wordt geen backup gemaakt</li>
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Typ <span className="font-bold text-red-600">{getBulkDeleteCount()}</span> om te bevestigen
                  </label>
                  <input
                    type="text"
                    value={bulkDeleteConfirmation}
                    onChange={(e) => setBulkDeleteConfirmation(e.target.value)}
                    placeholder={`Typ ${getBulkDeleteCount()} hier`}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={closeBulkDeleteModal}
                    className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Annuleer
                  </button>
                  <button
                    onClick={() => setBulkDeleteStep('confirm')}
                    disabled={bulkDeleteConfirmation !== String(getBulkDeleteCount())}
                    className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Volgende
                  </button>
                </div>
              </div>
            )}

            {bulkDeleteStep === 'confirm' && (
              <div className="space-y-4">
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 text-center">
                  <AlertTriangle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                  <p className="text-lg font-bold text-red-900 mb-2">
                    Laatste Waarschuwing
                  </p>
                  <p className="text-red-800">
                    {getBulkDeleteCount()} contact{getBulkDeleteCount() !== 1 ? 'en' : ''}
                    {bulkDeleteType === 'all' && ' (ALLE contacten)'}
                    {bulkDeleteType === 'unsubscribed' && ' (uitgeschreven contacten)'}
                    {bulkDeleteType === 'subscribed' && ' (actieve contacten)'}
                    {' '}worden nu permanent verwijderd.
                  </p>
                </div>

                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setBulkDeleteStep('input')}
                    disabled={bulkDeleting}
                    className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    Terug
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {bulkDeleting ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Verwijderen...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-5 h-5" />
                        Ja, Verwijder Permanent
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {contactToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Pencil className="w-6 h-6 text-[#FF6B35]" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#231F20] mb-2">
                  Contact bewerken
                </h2>
                <p className="text-sm text-gray-600 break-all">
                  {contactToEdit.email}
                </p>
              </div>
              <button
                onClick={() => setContactToEdit(null)}
                disabled={savingEdit}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Naam
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !savingEdit) confirmEditContact(); }}
                placeholder="Jan"
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="inline-flex items-center gap-1.5">
                  <Tag className="w-4 h-4" />
                  Groepen
                </span>
              </label>
              {editTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {editTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#FF6B35]/10 text-[#B84A1F]"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => setEditTags(editTags.filter((t) => t !== tag))}
                        className="hover:text-[#231F20] transition-colors"
                        aria-label={`Verwijder groep ${tag}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={editTagInput}
                onChange={(e) => setEditTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEditTag();
                  }
                }}
                onBlur={addEditTag}
                placeholder="Typ een groep en druk op Enter"
                list="mailinglist-groepen"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all"
              />
            </div>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setContactToEdit(null)}
                disabled={savingEdit}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Annuleer
              </button>
              <button
                onClick={confirmEditContact}
                disabled={savingEdit}
                className="px-6 py-3 bg-[#FF6B35] text-white font-medium rounded-lg hover:bg-[#E55A2B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {savingEdit ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Opslaan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {contactToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#231F20] mb-2">
                  Contact Verwijderen
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  Weet je zeker dat je dit contact permanent wilt verwijderen?
                </p>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="font-semibold text-[#231F20] text-sm mb-1">
                    {contactToDelete.name || contactToDelete.email}
                  </p>
                  <p className="text-xs text-gray-600">{contactToDelete.email}</p>
                  {contactToDelete.subscribed ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                      <CheckCircle className="w-3 h-3" />
                      Actief
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-2">
                      <UserX className="w-3 h-3" />
                      Uitgeschreven
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setContactToDelete(null)}
                disabled={deleting}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                Deze actie kan niet ongedaan gemaakt worden. Het contact wordt permanent verwijderd uit je mailinglijst.
              </p>
            </div>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setContactToDelete(null)}
                disabled={deleting}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Annuleer
              </button>
              <button
                onClick={confirmDeleteContact}
                disabled={deleting}
                className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Verwijderen...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Ja, Verwijder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {tagToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Tag className="w-6 h-6 text-[#FF6B35]" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#231F20] mb-2">
                  Groep verwijderen
                </h2>
                <p className="text-sm text-gray-600">
                  De groep wordt bij alle contacten weggehaald. De contacten zelf blijven gewoon in je lijst staan.
                </p>
              </div>
              <button
                onClick={() => setTagToRemove(null)}
                disabled={removingTag}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Welke groep wil je verwijderen?
              </label>
              <select
                value={tagToRemove}
                onChange={(e) => setTagToRemove(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all bg-white"
              >
                {allTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag} ({contacts.filter((c) => (c.tags || []).includes(tag)).length} contacten)
                  </option>
                ))}
              </select>
            </div>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setTagToRemove(null)}
                disabled={removingTag}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Annuleer
              </button>
              <button
                onClick={confirmRemoveTag}
                disabled={removingTag}
                className="px-6 py-3 bg-[#FF6B35] text-white font-medium rounded-lg hover:bg-[#E55A2B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {removingTag ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Verwijderen...
                  </>
                ) : (
                  <>
                    <Tag className="w-5 h-5" />
                    Verwijder groep
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
